/**
 * Client side of the Skylight push.
 *
 * The job id is what makes the push survivable: the POST may take 20-30s and
 * the client must not depend on it. Poll getSkylightJob instead.
 */
import { apiPost, apiGet } from './apiClient';

export interface SkylightPushSummaryDto {
  weekStart: string; recipes: number; mealsDeleted: number; mealsCreated: number;
  groceries: number; groceriesSkipped: number; mealsSkipped: boolean; skipReason?: string;
  mealsUnresolved?: number; unresolvedRecipeIds?: string[];
}

export interface SkylightJobDto {
  jobId: string;
  status: 'running' | 'done' | 'failed';
  phase: 'recipes' | 'deletions' | 'meals' | 'groceries' | null;
  done: number; total: number;
  summary?: SkylightPushSummaryDto;
  error?: string;
}

/**
 * A fresh job id, minted here rather than by the server.
 *
 * The push runs inside its own POST — 20-30s of writes — so the response only
 * arrives once the work is over. A client that waited for it would have nothing
 * to poll for the entire push, which is the whole point of the job document.
 * Naming the job before asking for it is what lets polling start immediately.
 *
 * Same idiom as the rest of the repo (deliveryImportUtils.ts): there is no uuid
 * dependency. The time prefix alone is 8+ characters, so the result always
 * satisfies the server's `[A-Za-z0-9_-]{8,64}`.
 */
export function newSkylightJobId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export async function startSkylightPush(
  weekStart: string,
  jobId: string
): Promise<{ jobId: string; summary?: SkylightPushSummaryDto }> {
  return apiPost('/api/skylight/push', { weekStart, jobId });
}

export async function getSkylightJob(jobId: string): Promise<SkylightJobDto> {
  return apiGet(`/api/skylight/status?jobId=${encodeURIComponent(jobId)}`);
}
